<template>
  <div
    v-if="open"
    class="modal fade show d-block promotion-modal"
    tabindex="-1"
    aria-modal="true"
    role="dialog"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ isEdit ? $t("promotion_page.edit_chip") : $t("promotion_page.new_chip") }}</span>
            <h5 class="modal-title fw-semibold mt-3 mb-1">
              {{ isEdit ? $t("promotion_page.update_title") : $t("promotion_page.create_title") }}
            </h5>
            <p class="text-muted mb-0 modal-copy">
              {{ $t("promotion_page.modal_description") }}
            </p>
          </div>
          <button type="button" class="btn-close shadow-none" :aria-label="$t('common.close')" @click="$emit('close')"></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <div class="promotion-tip-card mb-4">
            <div class="tip-icon">
              <i class="ri-megaphone-line"></i>
            </div>
            <div>
              <h6 class="mb-1">
                {{ isEdit ? $t("promotion_page.edit_tip_title") : $t("promotion_page.create_tip_title") }}
              </h6>
              <p class="mb-0 text-muted">
                {{ $t("promotion_page.tip_description") }}
              </p>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.code") }}</label>
                <span class="field-caption">{{ $t("promotion_page.code_caption") }}</span>
              </div>
              <div class="reference-input-wrap">
                <input
                  :value="form.code"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': errors.code }"
                  :placeholder="$t('promotion_page.code_placeholder')"
                  @input="$emit('update-field', 'code', ($event.target as HTMLInputElement).value)"
                />
                <button
                  v-if="!isEdit && canCreate"
                  type="button"
                  class="btn btn-light generate-code-btn"
                  :disabled="loading"
                  @click="$emit('generate-code')"
                >
                  <i class="ri-refresh-line me-1"></i>
                  {{ $t("promotion_page.generate_code") }}
                </button>
              </div>
              <div v-if="errors.code" class="invalid-feedback d-block">{{ errors.code[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.name") }}</label>
                <span class="field-caption">{{ $t("promotion_page.name_caption") }}</span>
              </div>
              <input
                :value="form.name"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.name }"
                :placeholder="$t('promotion_page.name_placeholder')"
                @input="$emit('update-field', 'name', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.name" class="invalid-feedback d-block">{{ errors.name[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.customer") }}</label>
                <span class="field-caption">{{ $t("promotion_page.customer_caption") }}</span>
              </div>
              <select
                :value="form.customer_id ?? ''"
                class="form-select modal-input"
                @change="$emit('update-field', 'customer_id', Number(($event.target as HTMLSelectElement).value) || null)"
              >
                <option value="">{{ $t("promotion_page.all_customers") }}</option>
                <option v-for="customer in customers" :key="customer.id" :value="customer.id">
                  {{ customer.name }}
                </option>
              </select>
            </div>

            <div class="col-md-3">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.discount_type") }}</label>
                <span class="field-caption">{{ $t("promotion_page.discount_type_caption") }}</span>
              </div>
              <select
                :value="form.discount_type"
                class="form-select modal-input"
                @change="$emit('update-field', 'discount_type', ($event.target as HTMLSelectElement).value)"
              >
                <option value="fixed">{{ $t("promotion_page.type_fixed") }}</option>
                <option value="percentage">{{ $t("promotion_page.type_percentage") }}</option>
              </select>
            </div>

            <div class="col-md-3">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.discount_value") }}</label>
                <span class="field-caption">{{ $t("promotion_page.discount_value_caption") }}</span>
              </div>
              <input
                :value="form.value"
                type="number"
                min="0"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.value }"
                :placeholder="$t('promotion_page.discount_value_placeholder')"
                @input="$emit('update-field', 'value', Number(($event.target as HTMLInputElement).value || 0))"
              />
              <div v-if="errors.value" class="invalid-feedback d-block">{{ errors.value[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.start_date") }}</label>
                <span class="field-caption">{{ $t("promotion_page.start_date_caption") }}</span>
              </div>
              <input
                :value="form.valid_from"
                type="date"
                class="form-control modal-input"
                @input="$emit('update-field', 'valid_from', ($event.target as HTMLInputElement).value)"
              />
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.end_date") }}</label>
                <span class="field-caption">{{ $t("promotion_page.end_date_caption") }}</span>
              </div>
              <input
                :value="form.valid_till"
                type="date"
                class="form-control modal-input"
                @input="$emit('update-field', 'valid_till', ($event.target as HTMLInputElement).value)"
              />
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.status") }}</label>
                <span class="field-caption">{{ $t("promotion_page.status_caption") }}</span>
              </div>
              <select
                :value="Number(form.status ?? 1)"
                class="form-select modal-input"
                @change="$emit('update-field', 'status', Number(($event.target as HTMLSelectElement).value))"
              >
                <option :value="1">{{ $t("promotion_page.status_active") }}</option>
                <option :value="0">{{ $t("promotion_page.status_inactive") }}</option>
              </select>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.valid_days") }}</label>
                <span class="field-caption">{{ $t("promotion_page.valid_days_caption") }}</span>
              </div>
              <div class="day-grid">
                <button
                  v-for="day in weekdayOptions"
                  :key="day.value"
                  type="button"
                  class="day-pill"
                  :class="{ active: form.valid_days.includes(day.value) }"
                  @click="$emit('toggle-day', day.value)"
                >
                  {{ day.label }}
                </button>
              </div>
              <div v-if="errors.valid_day" class="invalid-feedback d-block">{{ errors.valid_day[0] }}</div>
            </div>

            <div class="col-12">
              <div class="field-header">
                <label class="form-label fw-semibold mb-0">{{ $t("promotion_page.description") }}</label>
                <span class="field-caption">{{ $t("promotion_page.description_caption") }}</span>
              </div>
              <textarea
                :value="form.description"
                rows="4"
                class="form-control modal-input modal-textarea"
                :placeholder="$t('promotion_page.description_placeholder')"
                @input="$emit('update-field', 'description', ($event.target as HTMLTextAreaElement).value)"
              ></textarea>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
            <button type="button" class="btn btn-light px-4" @click="$emit('close')">
              {{ $t("common.cancel") }}
            </button>
            <button
              type="button"
              class="btn style-one px-4 h-55"
              :disabled="loading || !canSave"
              @click="$emit('save')"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? $t("promotion_page.saving") : isEdit ? $t("promotion_page.update_action") : $t("promotion_page.create_action") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Customer } from "@/services/customer";
import type { PromotionFormState } from "@/composables/usePromotion";
import { WEEKDAY_OPTIONS } from "@/utils/promotion";

export default defineComponent({
  name: "CreatePromotionModal",
  emits: ["close", "save", "generate-code", "update-field", "toggle-day"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object as PropType<PromotionFormState>,
      required: true,
    },
    customers: {
      type: Array as PropType<Customer[]>,
      required: true,
    },
    errors: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canCreate: {
      type: Boolean,
      default: false,
    },
    canUpdate: {
      type: Boolean,
      default: false,
    },
    canSave: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t } = useI18n();
    const weekdayOptions = computed(() =>
      WEEKDAY_OPTIONS.map((day) => ({
        ...day,
        label: t(`promotion_page.weekday_${day.value}`),
      }))
    );

    return {
      weekdayOptions,
    };
  },
});
</script>

<style scoped>
.promotion-modal {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
  font-size: 13px;
}

.promotion-tip-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  border: 1px solid #e6ebfb;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
}

.tip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: #eef2ff;
  color: #5d5fef;
  font-size: 20px;
}

.modal-copy {
  font-size: 14px;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  color: #6b7280;
  font-size: 12px;
}

.reference-input-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: stretch;
}

.modal-input {
  min-height: 52px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #fff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
  font-size: 14px;
}

.modal-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.modal-textarea {
  min-height: 132px;
  resize: vertical;
  padding-top: 16px;
}

.generate-code-btn {
  min-height: 52px;
  padding: 0 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #f8faff;
  font-weight: 600;
  white-space: nowrap;
}

.day-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
}

.day-pill {
  min-height: 48px;
  border-radius: 14px;
  border: 1px solid #d7def4;
  background: #f8faff;
  color: #334155;
  font-weight: 700;
}

.day-pill.active {
  border-color: #605dff;
  background: #eef2ff;
  color: #4338ca;
}

@media (max-width: 991px) {
  .day-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
